/**
 * Note: Use position fixed according to your needs
 * Desktop navbar is better positioned at the bottom
 * Mobile navbar is better positioned at bottom right.
 */
import React, { useRef, useState } from 'react';
import { IconLayoutNavbarCollapse } from '@tabler/icons-react';
import type { MotionValue } from 'motion/react';
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { cn } from '@epihub/web/src/tailwind/fn/cn';

export const FloatingDock = ({
  items,
  desktopClassName,
  mobileClassName,
  tooltipPosition = 'top',
}: {
  items: Array<{ title?: string; icon: React.ReactNode; href?: string; onClick?: () => void }>;
  desktopClassName?: string;
  mobileClassName?: string;
  tooltipPosition?: 'top' | 'bottom';
}) => {
  return (
    <>
      <FloatingDockDesktop items={items} className={desktopClassName} tooltipPosition={tooltipPosition} />
      <FloatingDockMobile items={items} className={mobileClassName} />
    </>
  );
};

const FloatingDockMobile = ({
  items,
  className,
}: {
  items: { title?: string; icon: React.ReactNode; href?: string; onClick?: () => void }[];
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('relative block md:hidden', className)}>
      <AnimatePresence>
        {open && (
          <motion.div
            layoutId='nav'
            className='absolute inset-x-0 bottom-full mb-2 flex flex-col gap-2'
          >
            {items.map((item, idx) => (
              <motion.div
                key={item.title ?? idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 10,
                  transition: {
                    delay: idx * 0.05,
                  },
                }}
                transition={{ delay: (items.length - 1 - idx) * 0.05 }}
              >
                <a
                  href={item.href}
                  onClick={item.onClick}
                  key={item.title ?? idx}
                  className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 dark:bg-neutral-900'
                  style={{ cursor: 'pointer' }}
                >
                  <div className='h-4 w-4'>{item.icon}</div>
                </a>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 dark:bg-neutral-800'
        style={{ cursor: 'pointer' }}
      >
        <IconLayoutNavbarCollapse className='h-5 w-5 text-neutral-500 dark:text-neutral-400' />
      </button>
    </div>
  );
};

const FloatingDockDesktop = ({
  items,
  className,
  tooltipPosition,
}: {
  items: { title?: string; icon: React.ReactNode; href?: string; onClick?: () => void }[];
  className?: string;
  tooltipPosition: 'top' | 'bottom';
}) => {
  let mouseX = useMotionValue(Infinity);
  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={cn(
        'mx-auto hidden h-16 items-end gap-4 rounded-2xl bg-gray-50 px-4 pb-3 md:flex dark:bg-neutral-900',
        className,
      )}
    >
      {items.map((item, idx) => (
        <IconContainer mouseX={mouseX} key={item.title ?? idx} tooltipPosition={tooltipPosition} {...item} />
      ))}
    </motion.div>
  );
};

function IconContainer({
  mouseX,
  title,
  icon,
  href,
  onClick,
  tooltipPosition,
}: {
  mouseX: MotionValue;
  icon: React.ReactNode;
  href?: string;
  title?: string;
  onClick?: () => void;
  tooltipPosition: 'top' | 'bottom';
}) {
  let ref = useRef<HTMLDivElement>(null);

  let distance = useTransform(mouseX, (val) => {
    let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };

    return val - bounds.x - bounds.width / 2;
  });

  let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
  let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

  let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
  let heightTransformIcon = useTransform(
    distance,
    [-150, 0, 150],
    [20, 40, 20],
  );

  let width = useSpring(widthTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let height = useSpring(heightTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  let widthIcon = useSpring(widthTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  let heightIcon = useSpring(heightTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const [hovered, setHovered] = useState(false);

  const tooltipClasses = tooltipPosition === 'top'
    ? 'absolute -top-8 left-1/2 w-fit rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white'
    : 'absolute -bottom-8 left-1/2 w-fit rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white';

  const tooltipInitial = tooltipPosition === 'top'
    ? { opacity: 0, y: 10, x: '-50%' }
    : { opacity: 0, y: -10, x: '-50%' };

  const tooltipAnimate = { opacity: 1, y: 0, x: '-50%' };

  const tooltipExit = tooltipPosition === 'top'
    ? { opacity: 0, y: 2, x: '-50%' }
    : { opacity: 0, y: -2, x: '-50%' };

  return (
    <a href={href} onClick={onClick} style={{ cursor: 'pointer' }}>
      <motion.div
        ref={ref}
        style={{ width, height }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className='relative flex aspect-square items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-800'
      >
        <AnimatePresence>
          {hovered && title && (
            <motion.div
              initial={tooltipInitial}
              animate={tooltipAnimate}
              exit={tooltipExit}
              className={tooltipClasses}
            >
              {title}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          style={{ width: widthIcon, height: heightIcon }}
          className='flex items-center justify-center'
        >
          {icon}
        </motion.div>
      </motion.div>
    </a>
  );
}
